import React, { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext(null);

// This component contains the core authentication logic and state
const AuthLogicProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true); // Start as true
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [hasPartialQuizInSessionStorage, setHasPartialQuizInSessionStorage] = useState(false);

  // Move fetchUserProfile outside useEffect so it can be used by refreshProfile
  const fetchUserProfile = async (userId) => {
    if (!userId) return { profileData: null, subStatus: null };
    try {
      console.log('AuthLogicProvider: Fetching profile for user:', userId);
      
      // Add timeout protection like in LoginForm
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile fetch timeout')), 8000); // 8 second timeout
      });
      
      const queryPromise = supabase
        .from('user_profiles')
        .select('quiz_completed, subscription_status, subscription_ends_at, token_balance, email, username, avatar_url, xp, level, id, voice_preferences, welness_goals')
        .eq('id', userId)
        .maybeSingle();
      
      const { data, error: profileError } = await Promise.race([queryPromise, timeoutPromise]);
      
      if (profileError) {
        if (profileError.code === 'PGRST116') {
          console.log("AuthLogicProvider: No profile found for user (PGRST116):", userId);
          return { profileData: null, subStatus: null };
        }
        console.error('Error fetching user profile in AuthLogicProvider:', profileError);
        return { profileData: null, subStatus: null };
      }
      if (!data) {
        console.log("AuthLogicProvider: No profile data found for user:", userId);
        return { profileData: null, subStatus: null };
      }

      let subStatus = data.subscription_status;
      if (data.subscription_ends_at) {
        const endDate = new Date(data.subscription_ends_at);
        if (endDate < new Date()) {
          subStatus = 'expired';
        } else if (data.subscription_status === 'canceled') {
          // Canceled subscription is still active until end date
          subStatus = 'active';
        }
      }
      console.log("AuthLogicProvider: Profile fetched for user", userId, {
        subscription_status: data.subscription_status,
        subscription_ends_at: data.subscription_ends_at,
        quiz_completed: data.quiz_completed,
        computed_subStatus: subStatus,
        endDateFuture: data.subscription_ends_at ? new Date(data.subscription_ends_at) > new Date() : 'no_end_date',
        originalStatus: data.subscription_status,
        finalComputedStatus: subStatus,
        logicApplied: data.subscription_status === 'canceled' && data.subscription_ends_at && new Date(data.subscription_ends_at) > new Date() ? 'CONVERTED_TO_ACTIVE' : 'NO_CHANGE'
      });
      return { profileData: data, subStatus };
    } catch (err) {
      console.error('Error in fetchUserProfile in AuthLogicProvider:', err);
      
      // Handle timeout specifically
      if (err.message === 'Profile fetch timeout') {
        console.log('AuthLogicProvider: Profile fetch timed out - continuing without profile');
      }
      
      // Always return a valid response, never throw
      return { profileData: null, subStatus: null };
    }
  };

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;
    let safetyTimeoutId = null;
    
    console.log("AuthLogicProvider: useEffect for auth state change mounted.");
    
    // Ensure loading is true when the effect runs for the first time.
    if (mounted) { 
        setLoading(true); 
    }
    
    // Safety timeout to ensure loading is always resolved within 15 seconds
    safetyTimeoutId = setTimeout(() => {
      if (mounted) {
        console.warn('AuthLogicProvider: Safety timeout triggered - forcing loading to false');
        setLoading(false);
      }
    }, 15000);

    const checkPartialQuiz = () => {
      const partialQuizData = sessionStorage.getItem('quizAnswers');
      const currentQuestion = sessionStorage.getItem('currentQuizQuestion');
      return (partialQuizData && partialQuizData !== '[]') || (currentQuestion !== null);
    };

    // Check initial session
    supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      if (!mounted) return;
      console.log("AuthLogicProvider: getSession() completed. Session:", !!currentSession);
      if (currentSession) {
        setSession(currentSession);
        setHasPartialQuizInSessionStorage(checkPartialQuiz());
        const { profileData, subStatus } = await fetchUserProfile(currentSession.user.id);
        if (mounted) {
          setProfile(profileData);
          setSubscriptionStatus(subStatus);
          // Set loading to false here for initial session
          setLoading(false);
          console.log("AuthLogicProvider: Initial session processed, loading set to false.");
        }
      } else {
        if (mounted) {
          setLoading(false);
          console.log("AuthLogicProvider: No initial session, loading set to false.");
        }
      }
    }).catch(err => {
        if(mounted) {
            console.error("AuthLogicProvider: Error in initial getSession:", err);
            setError(err);
            setLoading(false);
        }
    });
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        // Clear any existing timeout to debounce rapid auth state changes
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        
        console.log("AuthLogicProvider: onAuthStateChange event:", event, "session:", !!session);
        
        // Debounce the auth state processing to prevent race conditions
        timeoutId = setTimeout(async () => {
          if (!mounted) return;
          
          setSession(session);
          setHasPartialQuizInSessionStorage(checkPartialQuiz());
          
          if (session?.user) {
            // Only update last activity on actual sign in, not every auth state change
            if (event === 'SIGNED_IN') {
              try {
                // Use update instead of upsert to avoid RLS issues
                // The database trigger should have already created the profile
                const { error } = await supabase
                  .from('user_profiles')
                  .update({
                    last_activity_at: new Date().toISOString()
                  })
                  .eq('id', session.user.id);
                
                if (error) {
                  console.log("AuthLogicProvider: Profile may not exist yet, skipping last_activity_at update");
                } else {
                  console.log("AuthLogicProvider: Updated last_activity_at for user:", session.user.id);
                }
              } catch (error) {
                console.error("AuthLogicProvider: Error updating last_activity_at:", error);
              }
            }
            
            const { profileData, subStatus } = await fetchUserProfile(session.user.id);
            if (mounted) {
              setProfile(profileData);
              setSubscriptionStatus(subStatus);
            }
          } else {
            if (mounted) {
              setProfile(null);
              setSubscriptionStatus(null);
            }
          }
          
          if (mounted) {
            setLoading(false); 
            console.log("AuthLogicProvider: Auth state processed, loading set to false.");
          }
        }, 200); // Increased debounce to 200ms to reduce rapid successive calls
      }
    );

    const handleStorageChange = () => {
      if (mounted) setHasPartialQuizInSessionStorage(checkPartialQuiz());
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      subscription?.unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
      console.log("AuthLogicProvider: useEffect for auth state change unmounted.");
    };
  }, []); 

  const refreshProfile = async () => {
    if (!session?.user?.id) {
      console.log('AuthLogicProvider: No session available for refresh');
      return;
    }
    
    console.log('AuthLogicProvider: Manually refreshing profile...');
    const { profileData, subStatus } = await fetchUserProfile(session.user.id);
    setProfile(profileData);
    setSubscriptionStatus(subStatus);
    console.log('AuthLogicProvider: Profile refresh completed');
  };

  const value = {
    session,
    profile,
    loading,
    error,
    subscriptionStatus,
    hasPartialQuizInSessionStorage,
    refreshProfile,
    signOut: async () => {
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (error) {
        console.log('SignOut error (expected if already signed out):', error.message);
        // Don't throw error for logout issues, just ensure user is redirected
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthLogicProvider as AuthProvider };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) { 
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
